import React from 'react';
import { X, Eye } from 'lucide-react';
import { FaFacebook, FaInstagram, FaLinkedinIn, FaGoogle, FaEnvelope, FaVideo, FaImage, FaFileAlt, FaGlobe, FaWhatsapp, FaTelegram, FaTiktok } from 'react-icons/fa';

const AD_TYPE_META = {
  facebook_ad: { label: 'Facebook Ad', icon: FaFacebook, color: '#1877F2' },
  instagram_ad: { label: 'Instagram Ad', icon: FaInstagram, color: '#E4405F' },
  linkedin_ad: { label: 'LinkedIn Ad', icon: FaLinkedinIn, color: '#0A66C2' },
  google_ads: { label: 'Google Ad', icon: FaGoogle, color: '#4285F4' },
  product_description: { label: 'Product Description', icon: FaFileAlt, color: '#F59E0B' },
  email_marketing: { label: 'Email Campaign', icon: FaEnvelope, color: '#EA4335' },
  video_promo: { label: 'Video Promo', icon: FaVideo, color: '#FF0000' },
  image_promo: { label: 'Image Ad', icon: FaImage, color: '#10B981' },
  whatsapp_ad: { label: 'WhatsApp Ad', icon: FaWhatsapp, color: '#25D366' },
  telegram_ad: { label: 'Telegram Ad', icon: FaTelegram, color: '#0088CC' },
  tiktok_ad: { label: 'TikTok Ad', icon: FaTiktok, color: '#000000' },
  social_media: { label: 'Social Post', icon: FaGlobe, color: '#8B5CF6' },
};

// Individual preview card renderers
const FacebookPreview = ({ formData, themeAccent }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden text-left">
    <div className="p-3 flex items-center gap-2">
      <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-xs font-bold text-slate-500">
        {formData.product_name?.charAt(0) || 'A'}
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-800 dark:text-white leading-tight">{formData.product_name || 'Your Brand'}</p>
        <p className="text-[10px] text-slate-400">Sponsored</p>
      </div>
    </div>
    <div className="px-3 pb-2">
      <p className="text-[11px] text-slate-600 dark:text-slate-300 line-clamp-3 leading-relaxed">
        {formData.product_description?.slice(0, 150) || 'Your product description will appear here. Add details to see a live preview.'}
        {formData.product_description?.length > 150 ? '...' : ''}
      </p>
    </div>
    <div className={`h-28 ${themeAccent.bgLight} flex items-center justify-center`}>
      {formData.product_tagline ? (
        <p className={`text-sm font-bold ${themeAccent.text} px-4 text-center`}>{formData.product_tagline}</p>
      ) : (
        <FaImage size={24} className={themeAccent.textMuted} />
      )}
    </div>
    <div className="p-3 flex justify-between border-t border-slate-100 dark:border-slate-700">
      <span className="text-[10px] text-slate-400">Learn More</span>
      {formData.price && <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">{formData.price}</span>}
    </div>
  </div>
);

const InstagramPreview = ({ formData, themeAccent }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden text-left">
    <div className="p-2.5 flex items-center gap-2">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-yellow-400 flex items-center justify-center text-[10px] font-bold text-white">
        {formData.product_name?.charAt(0) || 'A'}
      </div>
      <p className="text-[11px] font-semibold text-slate-800 dark:text-white">{formData.product_name || 'your_brand'}</p>
    </div>
    <div className={`h-32 ${themeAccent.bgLight} flex items-center justify-center`}>
      {formData.product_tagline ? (
        <p className={`text-sm font-bold ${themeAccent.text} px-4 text-center`}>{formData.product_tagline}</p>
      ) : (
        <FaImage size={24} className={themeAccent.textMuted} />
      )}
    </div>
    <div className="p-2.5">
      <p className="text-[11px] text-slate-700 dark:text-slate-300 line-clamp-2">
        <span className="font-semibold">{formData.product_name || 'your_brand'}</span>{' '}
        {formData.product_description?.slice(0, 100) || 'Caption goes here...'}
      </p>
    </div>
  </div>
);

const GoogleAdPreview = ({ formData, themeAccent }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-left">
    <p className="text-[10px] text-slate-400 mb-0.5">Ad · www.{(formData.product_name || 'example').toLowerCase().replace(/\s+/g, '')}.com</p>
    <p className="text-sm font-medium text-blue-600 dark:text-blue-400 leading-tight mb-1">
      {formData.product_name || 'Your Product'} {formData.price ? `- ${formData.price}` : '- Official Site'}
    </p>
    <p className="text-[11px] text-slate-600 dark:text-slate-400 line-clamp-2 leading-relaxed">
      {formData.product_description?.slice(0, 120) || 'Your ad description will be generated by AI based on your product details.'}
    </p>
  </div>
);

const EmailPreview = ({ formData, themeAccent }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden text-left">
    <div className={`px-3 py-2 ${themeAccent.bgLight} border-b border-slate-200 dark:border-slate-700`}>
      <p className="text-[10px] text-slate-400">Subject:</p>
      <p className="text-xs font-semibold text-slate-800 dark:text-white">
        {formData.product_tagline || `Discover ${formData.product_name || 'Our Product'}`}
      </p>
    </div>
    <div className="p-3">
      <p className="text-[11px] text-slate-600 dark:text-slate-300 line-clamp-4 leading-relaxed">
        {formData.product_description?.slice(0, 200) || 'Email body content will be AI-generated based on your product details and target audience.'}
      </p>
      <div className={`mt-2 inline-block px-3 py-1 rounded text-[10px] font-semibold text-white ${themeAccent.bgSolid}`}>
        Learn More
      </div>
    </div>
  </div>
);

const VideoPreview = ({ formData, themeAccent }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden text-left">
    <div className={`h-24 ${themeAccent.bgLight} flex items-center justify-center relative`}>
      <div className={`w-10 h-10 rounded-full ${themeAccent.bgSolid} bg-opacity-80 flex items-center justify-center`}>
        <div className="w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[10px] border-l-white ml-0.5" />
      </div>
      <div className="absolute bottom-1 right-2 flex gap-1">
        <span className="text-[9px] bg-black/60 text-white px-1 rounded">{formData.video_duration || 8}s</span>
        <span className="text-[9px] bg-black/60 text-white px-1 rounded">{formData.video_resolution || '720p'}</span>
      </div>
    </div>
    <div className="p-2.5">
      <p className="text-[11px] font-medium text-slate-700 dark:text-slate-200">
        {formData.product_name || 'Product'} — AI Video
      </p>
      <p className="text-[10px] text-slate-400 mt-0.5">
        Model: {formData.video_model === 'veo3' ? 'Premium' : formData.video_model === 'veo3_fast' ? 'Fast' : formData.video_model === 'ultra_video' ? 'Ultra' : 'Quick'} · {formData.video_aspect_ratio || '16:9'}
      </p>
    </div>
  </div>
);

const ImageAdPreview = ({ formData, themeAccent }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden text-left">
    <div className={`h-28 ${themeAccent.bgLight} flex flex-col items-center justify-center p-3`}>
      {formData.product_tagline ? (
        <>
          <p className={`text-xs font-bold ${themeAccent.text} text-center mb-1`}>{formData.product_tagline}</p>
          <p className="text-[10px] text-slate-500 text-center">{formData.product_name || ''}</p>
        </>
      ) : (
        <FaImage size={24} className={themeAccent.textMuted} />
      )}
    </div>
    <div className="p-2.5">
      <p className="text-[10px] text-slate-400">
        Model: {formData.image_model === 'gpt4o-image' ? 'Ultra' : formData.image_model === 'nano-banana-pro' ? 'Premium' : formData.image_model === 'freepik' ? 'Best Value' : 'Standard'}
        {formData.text_overlay_method === 'overlay' ? ' · Text Overlay' : ' · AI Text'}
      </p>
    </div>
  </div>
);

const GenericPreview = ({ formData, themeAccent, label }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-left">
    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">{formData.product_name || 'Your Product'}</p>
    <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed">
      {formData.product_description?.slice(0, 120) || `AI-generated ${label.toLowerCase()} content will appear here.`}
    </p>
    {formData.price && <p className="text-[10px] font-semibold text-slate-600 dark:text-slate-300 mt-1">{formData.price}</p>}
  </div>
);

const PREVIEW_RENDERERS = {
  facebook_ad: FacebookPreview,
  instagram_ad: InstagramPreview,
  google_ads: GoogleAdPreview,
  email_marketing: EmailPreview,
  video_promo: VideoPreview,
  image_promo: ImageAdPreview,
};

export const AdPreviewPanel = ({ formData, selectedAdTypes, isOpen, onClose, colorTheme }) => {
  if (!isOpen) return null;

  const themeAccent = colorTheme === 'purple'
    ? { bgLight: 'bg-purple-50 dark:bg-purple-900/20', bgSolid: 'bg-purple-600', text: 'text-purple-700 dark:text-purple-300', textMuted: 'text-purple-300 dark:text-purple-600', border: 'border-purple-200 dark:border-purple-700' }
    : colorTheme === 'blue'
    ? { bgLight: 'bg-blue-50 dark:bg-blue-900/20', bgSolid: 'bg-blue-600', text: 'text-blue-700 dark:text-blue-300', textMuted: 'text-blue-300 dark:text-blue-600', border: 'border-blue-200 dark:border-blue-700' }
    : { bgLight: 'bg-emerald-50 dark:bg-emerald-900/20', bgSolid: 'bg-emerald-600', text: 'text-emerald-700 dark:text-emerald-300', textMuted: 'text-emerald-300 dark:text-emerald-600', border: 'border-emerald-200 dark:border-emerald-700' };

  const activeTypes = selectedAdTypes.filter(t => AD_TYPE_META[t]);
  const hasContent = formData.product_name || formData.product_description;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ad-preview-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-2xl overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className={`sticky top-0 z-10 px-5 py-4 border-b ${themeAccent.border} bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className={`w-4 h-4 ${themeAccent.text}`} />
              <h3 className="text-sm font-bold text-slate-800 dark:text-white">Live Preview</h3>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${themeAccent.bgLight} ${themeAccent.text}`}>
                {activeTypes.length} types
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="close-preview-btn"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          {!hasContent && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2">
              Fill in product details to see live previews
            </p>
          )}
        </div>

        {/* Preview Cards */}
        <div className="p-5 space-y-5">
          {activeTypes.length === 0 ? (
            <div className="text-center py-12">
              <Eye className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-500 dark:text-slate-400">Select ad types to preview</p>
            </div>
          ) : (
            activeTypes.map(type => {
              const meta = AD_TYPE_META[type];
              const Renderer = PREVIEW_RENDERERS[type] || GenericPreview;
              const Icon = meta.icon;
              return (
                <div key={type}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} style={{ color: meta.color }} />
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{meta.label}</span>
                  </div>
                  <Renderer formData={formData} themeAccent={themeAccent} label={meta.label} />
                </div>
              );
            })
          )}

          {/* Summary card */}
          {hasContent && activeTypes.length > 0 && (
            <div className={`p-3 rounded-xl ${themeAccent.bgLight} border ${themeAccent.border}`}>
              <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Campaign Summary</p>
              <div className="grid grid-cols-2 gap-y-1 text-[10px] text-slate-500 dark:text-slate-400">
                <span>Ad Types:</span><span className="font-medium text-slate-700 dark:text-slate-300">{activeTypes.length}</span>
                {formData.enable_personas && (
                  <>
                    <span>Personas:</span><span className="font-medium text-slate-700 dark:text-slate-300">{formData.persona_count === 'ai_determined' ? 'AI-determined' : formData.persona_count}</span>
                  </>
                )}
                {formData.category && (
                  <>
                    <span>Category:</span><span className="font-medium text-slate-700 dark:text-slate-300">{formData.category}</span>
                  </>
                )}
                {formData.brand_voice && (
                  <>
                    <span>Voice:</span><span className="font-medium text-slate-700 dark:text-slate-300 capitalize">{formData.brand_voice}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.25s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AdPreviewPanel;
